import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { listHotelActivity, type HotelActivityEntry } from '../features/hotel-activity/api.js';
import { Card } from '../components/ui/card.js';

function fmt(iso: string): string {
  return new Date(iso).toLocaleString('hu-HU', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const ACTION_LABEL: Record<string, string> = {
  'hotel.create': 'Hotel létrehozva',
  'hotel.update': 'Hotel frissítve',
  'hotel.delete': 'Hotel törölve',
  'plan.create': 'Terv létrehozva',
  'plan.update': 'Terv frissítve',
  'plan.delete': 'Terv törölve',
  'post.create': 'Poszt létrehozva',
  'post.update': 'Poszt módosítva',
  'post.delete': 'Poszt törölve',
  'post.apply_action': 'Poszt státusza változott',
  'approval_batch.create': 'Jóváhagyási csomag elküldve',
  'approval_batch.revoke': 'Jóváhagyási csomag visszavonva',
};

const TARGET_LABEL: Record<string, string> = {
  hotels: 'hotel',
  monthly_plans: 'terv',
  posts: 'poszt',
  approval_batches: 'jóváhagyás',
};

function ActivityRow({ entry }: { entry: HotelActivityEntry }) {
  const label = ACTION_LABEL[entry.action] ?? entry.action;
  const targetLabel = entry.targetType
    ? (TARGET_LABEL[entry.targetType] ?? entry.targetType)
    : null;
  return (
    <li className="flex items-start gap-3 border-b border-[var(--app-divider)] py-2.5 last:border-b-0">
      <div className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand-500)]" />
      <div className="min-w-0 flex-1">
        <div className="text-body text-[var(--ink-700)]">{label}</div>
        <div className="mt-0.5 text-meta text-[var(--fg-faint)]">
          {fmt(entry.occurredAt)}
          {entry.actorEmail && ` · ${entry.actorEmail}`}
          {targetLabel && ` · ${targetLabel}`}
        </div>
      </div>
    </li>
  );
}

function HotelActivityPage() {
  const { hotelId } = Route.useParams();
  const q = useQuery({
    queryKey: ['hotel-activity', hotelId],
    queryFn: () => listHotelActivity(hotelId, 100),
  });

  return (
    <div className="space-y-4">
      <div>
        <Link
          to="/app/hotels/$hotelId"
          params={{ hotelId }}
          className="text-meta text-[var(--fg-faint)] hover:underline"
        >
          ← Hotel
        </Link>
        <h1 className="mt-1 text-h1 font-semibold text-[var(--ink-700)]">Aktivitás</h1>
        <p className="mt-1 text-meta text-[var(--fg-faint)]">Utolsó 100 esemény erre a hotelre.</p>
      </div>
      <Card>
        {q.isLoading && <div className="text-body text-[var(--fg-faint)]">Betöltés…</div>}
        {q.error && (
          <div className="text-body text-[var(--danger)]">{(q.error as Error).message}</div>
        )}
        {q.data && q.data.length === 0 && (
          <div className="text-body text-[var(--fg-faint)]">Még nincs rögzített esemény.</div>
        )}
        {q.data && q.data.length > 0 && (
          <ul>
            {q.data.map((e) => (
              <ActivityRow key={e.id} entry={e} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/app/hotels/$hotelId/activity')({
  component: HotelActivityPage,
});
